import { Navigate, Route, Routes } from 'react-router-dom-v5-compat';

import { useLocationBlocker } from 'src/lib/hooks';

import { isApplicationModeInfinity } from './data/system';
import { ProjectLinkAccountGate } from './services/ProjectLinkAccountGate';
import { BrandBriefsPage } from './views/BrandBriefsPage';
import { BrandTermsPage } from './views/BrandTermsPage';
import { ChannelsResourcesPage } from './views/ChannelsResourcesPage';
import { CheckoutPage } from './views/CheckoutPage';
import { CopilotImportPage } from './views/CopilotImportPage';
import { CopyAnalyticsPage } from './views/CopyAnalyticsPage';
import { CustomModelsPage } from './views/CustomModelsPage';
import { DashboardPage } from './views/DashboardPage';
import { ExtensionOnboardingPage } from './views/ExtensionOnboardingPage';
import { ExtensionUninstalledPage } from './views/ExtensionUninstalledPage';
import { ExternalInfinityExtensionIframe } from './views/ExternalInfinityExtensionIframe';
import { ExternalSignupPage } from './views/ExternalSignupPage';
import FileViewerPage from './views/FileViewerPage';
import { FormatPage } from './views/FormatPage';
import { ImageStylePage } from './views/ImageStylePage';
import { IntegrationAuthCallback } from './views/IntegrationAuthCallback';
import LockedOutPage from './views/LockedOutPage';
import MobileDevicePage from './views/MobileDevicePage';
import { MultiPromptBuilderPage } from './views/MultiPromptBuilderPage';
import NotVerifiedPage from './views/NotVerifiedPage';
import { OAuthConsentPage } from './views/OAuthConsentPage';
import { PerformanceInsightsDataPage } from './views/PerformanceInsightsDataPage';
import { PerformanceInsightsPage } from './views/PerformanceInsightsPage';
import { PersonasPage } from './views/PersonasPage';
import { PrivateRoute } from './views/PrivateRoute';
import ProjectPage from './views/ProjectPage';
import { ProjectsPage } from './views/ProjectsPage';
import { PublicRoute } from './views/PublicRoute';
import RootPage from './views/RootPage';
import SettingsPage from './views/SettingsPage';
import { SubscriptionPage } from './views/SubscriptionPage';
import { TalkingPointsPage } from './views/TalkingPointsPage';
import { TemplateBuilder } from './views/TemplateBuilder';
import { ToneOfVoicePage } from './views/ToneOfVoicePage';
import { UsageReportDashboard } from './views/UsageReportDashboard';
import { WebsiteAutomationPage } from './views/WebsiteAutomationPage';
import { renderAccountPublicRoutes } from './publicRoutes';
import { ROUTES } from './routes';

// `react-router-dom-v5-compat`(it is official migration package) is used because `react-router-last-location`
// doesn't support react-router v6 so we need to use react-router v5
// After `react-router-last-location` will migrate to react-router v6, the following should be done:
//   1. uninstall `react-router-dom-v5-compat`, `react-router` and `react-router-dom` from package.json
//   2. install `react-router-dom`
//   3. remove `<CompatRouter>` from `App.js`
//   4. rewrite useLocationBlocker() using react-router v6
// Migration guide https://reactrouter.com/docs/en/v6/upgrading/v5

export const AppRouting = () => {
  useLocationBlocker();

  // Platform embedded in Extension really needs only one "view", and should not support redirections to anywhere else
  if (isApplicationModeInfinity()) {
    return <ExternalInfinityExtensionIframe />;
  }

  return (
    <Routes>
      {renderAccountPublicRoutes()}

      {[
        ROUTES.LINKEDIN_AUTH_CALLBACK,
        ROUTES.FACEBOOK_AUTH_CALLBACK,
        ROUTES.GOOGLE_AUTH_CALLBACK,
        ROUTES.TWITTER_AUTH_CALLBACK,
        ROUTES.TWITTER_SOCIAL_AUTH_CALLBACK,
        ROUTES.HUBSPOT_AUTH_CALLBACK,
        ROUTES.SPRINKLR_AUTH_CALLBACK,
        ROUTES.SALESFORCE_AUTH_CALLBACK,
        ROUTES.SALESFORCE_CRM_AUTH_CALLBACK,
        ROUTES.MS_DYNAMICS_CRM_AUTH_CALLBACK,
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <PublicRoute displayNavigation>
              <IntegrationAuthCallback />
            </PublicRoute>
          }
        />
      ))}

      <Route
        path={ROUTES.EXTERNAL_SIGNUP}
        element={
          <PublicRoute displayNavigation>
            <ExternalSignupPage />
          </PublicRoute>
        }
      />

      <Route
        path={ROUTES.DASHBOARD}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.OAUTH_CONSENT}
        element={
          <PrivateRoute>
            <OAuthConsentPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.COPILOT_IMPORT}
        element={
          <PrivateRoute>
            <CopilotImportPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.NOT_VERIFIED}
        element={
          <PrivateRoute>
            <NotVerifiedPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.TALKING_POINTS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <TalkingPointsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.INTEGRATIONS_RESOURCES}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <ChannelsResourcesPage />
          </PrivateRoute>
        }
      />

      <Route
        path={`${ROUTES.PERFORMANCE_INSIGHTS}/:channel`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <PerformanceInsightsPage />
          </PrivateRoute>
        }
      />

      <Route
        path={`${ROUTES.PERFORMANCE_INSIGHTS_DATA}/:channel`}
        element={
          <PrivateRoute
            displayNotifications
            whiteBackground
          >
            <PerformanceInsightsDataPage />
          </PrivateRoute>
        }
      />

      <Route
        path={`${ROUTES.ANALYTICS}/:viewType?`}
        element={
          <PrivateRoute displayNotifications>
            <CopyAnalyticsPage />
          </PrivateRoute>
        }
      />

      <Route
        path={ROUTES.WEBSITE_AUTOMATION}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <WebsiteAutomationPage />
          </PrivateRoute>
        }
      />

      <Route
        path={`${ROUTES.BRAND_VOICE}/:brandVoiceId${ROUTES.BRAND_TERMS}`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <BrandTermsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={`${ROUTES.BRAND_VOICE}/:brandVoiceId${ROUTES.BRAND_BRIEFS}`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <BrandBriefsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={`${ROUTES.BRAND_VOICE}/:brandVoiceId${ROUTES.TONE_OF_VOICE}`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <ToneOfVoicePage />
          </PrivateRoute>
        }
      />
      <Route
        path={`${ROUTES.BRAND_VOICE}/:brandVoiceId${ROUTES.CUSTOM_MODELS}`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <CustomModelsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={`${ROUTES.BRAND_VOICE}/:brandVoiceId${ROUTES.PERSONAS}`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <PersonasPage />
          </PrivateRoute>
        }
      />
      <Route
        path={`${ROUTES.BRAND_VOICE}/:brandVoiceId${ROUTES.FORMAT}`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <FormatPage />
          </PrivateRoute>
        }
      />

      <Route
        path={`${ROUTES.VISUAL_LANGUAGE}/:visualLanguageId${ROUTES.IMAGE_STYLE}`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <ImageStylePage />
          </PrivateRoute>
        }
      />

      <Route
        path={ROUTES.PROJECTS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
            exact
          >
            <ProjectsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={`${ROUTES.PROJECTS}/:projectId`}
        element={
          <ProjectLinkAccountGate>
            <PrivateRoute
              displayNotifications
              onlyImportantNotifications
              floatingNotifications
            >
              <ProjectPage />
            </PrivateRoute>
          </ProjectLinkAccountGate>
        }
      />

      <Route
        path={ROUTES.TEMPLATE_BUILDER}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <TemplateBuilder />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.MULTI_PROMPT_BUILDER}
        element={
          <PrivateRoute displayNotifications>
            <MultiPromptBuilderPage />
          </PrivateRoute>
        }
      />

      <Route
        path={ROUTES.SETTINGS_USAGE_REPORT}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <UsageReportDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.SETTINGS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <SettingsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.MOBILE}
        element={
          <PrivateRoute>
            <MobileDevicePage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.LOCKED}
        element={
          <PrivateRoute displayNavigation>
            <LockedOutPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.CHECKOUT}
        element={
          <PrivateRoute unAuthRedirectPath={ROUTES.SIGN_UP}>
            <CheckoutPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.UPGRADE}
        element={
          <PrivateRoute>
            <SubscriptionPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.EXTENSION_ONBOARDING}
        element={
          <PrivateRoute>
            <ExtensionOnboardingPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.EXTENSION_UNINSTALLED}
        element={
          <PrivateRoute>
            <ExtensionUninstalledPage />
          </PrivateRoute>
        }
      />

      <Route
        path={`${ROUTES.FILE_VIEWER}/:type`}
        element={
          <PrivateRoute>
            <FileViewerPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.ROOT}
        element={
          <PrivateRoute>
            <RootPage />
          </PrivateRoute>
        }
      />
      <Route
        path="*"
        element={
          <Navigate
            to={ROUTES.ROOT}
            replace
          />
        }
      />
    </Routes>
  );
};
